import {PageDataContextProvider} from '@github-ui/pull-request-page-data-tooling/page-data-context'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Blankslate} from '@primer/react/experimental'
import {getEntityCompositeID} from './entity-helpers'
import {Finding} from './Finding'
import {FindingsFilterBar, useFindingsFilter} from './FindingsFilterBar'
import pageStyles from './FindingsPage.module.css'
import {FindingsSummary} from './FindingsSummary'
import type {FindingsPayload} from './types'
import {useIgnoreFindings} from './use-ignore-findings'

interface CopilotCodeReviewFindingsProps extends FindingsPayload {
  hideSummary?: boolean
}

export const CopilotCodeReviewFindings: React.FC<CopilotCodeReviewFindingsProps> = props => (
  <ErrorBoundary fallback={null}>
    <FindingsList {...props} />
  </ErrorBoundary>
)

export const FindingsList: React.FC<CopilotCodeReviewFindingsProps> = ({
  findings,
  pullRequestBasePath,
  ignoredEntityIds,
  overviewSummary,
  mergeReadiness,
  hideSummary = false,
}) => {
  const {openFindings, ignoredFindings, handleDismiss, handleReopen, handleDismissAll, handleReopenAll} =
    useIgnoreFindings(findings, pullRequestBasePath, ignoredEntityIds)

  const filterProps = useFindingsFilter(openFindings, ignoredFindings)
  const hasNonVisibilityFilters =
    ('selectedSeverities' in filterProps && filterProps.selectedSeverities.size > 0) ||
    ('selectedSources' in filterProps && filterProps.selectedSources.size > 0)

  if (openFindings.length === 0 && ignoredFindings.length === 0) {
    return (
      <Blankslate>
        <Blankslate.Heading>No findings</Blankslate.Heading>
        <Blankslate.Description>There are no findings for this pull request</Blankslate.Description>
      </Blankslate>
    )
  }

  return (
    <PageDataContextProvider basePageDataUrl={pullRequestBasePath}>
      <div className={pageStyles.PageLayout}>
        {!hideSummary && <FindingsSummary mergeReadiness={mergeReadiness} overviewSummary={overviewSummary} />}
        <FindingsFilterBar
          openCount={openFindings.length}
          resolvedCount={ignoredFindings.length}
          onReopenAll={handleReopenAll}
          onDismissAll={handleDismissAll}
          {...filterProps}
        />
        {filterProps.displayedFindings.length === 0 && (
          <Blankslate className="border rounded-3">
            <Blankslate.Heading>
              {hasNonVisibilityFilters
                ? 'No findings match your filters'
                : filterProps.selectedVisibility === 'open'
                  ? 'All findings resolved'
                  : 'No resolved findings'}
            </Blankslate.Heading>
            <Blankslate.Description>
              {hasNonVisibilityFilters
                ? 'Try changing or clearing the filters to see more findings'
                : filterProps.selectedVisibility === 'open'
                  ? 'All findings for this pull request have been resolved'
                  : 'There are no resolved findings for this pull request'}
            </Blankslate.Description>
          </Blankslate>
        )}
        <div>
          {filterProps.displayedFindings.map(finding => (
            <Finding
              key={getEntityCompositeID(finding)}
              basePath={pullRequestBasePath}
              onReopen={handleReopen}
              onDismiss={handleDismiss}
              resolved={filterProps.selectedVisibility === 'resolved'}
              {...finding}
            />
          ))}
        </div>
      </div>
    </PageDataContextProvider>
  )
}
